// ---------------------------------------------------------------------------
// Prezio Onboard Widget — ThemeContext
// ---------------------------------------------------------------------------
// Applies CSS Custom Properties to the widget root element based on the
// ThemeConfig provided at init time. Handles light/dark/system color schemes.
// ---------------------------------------------------------------------------

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ColorScheme, ThemeConfig } from '@/types/config';
import { adaptToDark, readableForeground, shadeDirection, toRgb } from '@/utils/color';

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export interface ThemeContextValue {
  /** Resolved color scheme (never 'system' — always 'light' or 'dark'). */
  colorScheme: 'light' | 'dark';
  /** Logo URL provided by the host app, if any. */
  logo: string | null;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ---------------------------------------------------------------------------
// applyTheme — sets CSS Custom Properties on the root element
// ---------------------------------------------------------------------------
// Only the raw inputs are set here. Surfaces, borders, muted text and accent
// tints are derived from them in variables.css, so they follow automatically
// in both color schemes. Unparseable colors are skipped rather than written —
// an invalid value would break every color-mix() that depends on it.
//
// Host colors describe the light scheme. In dark mode the background/text pair
// is re-mapped rather than used verbatim, otherwise a host's light-mode text
// stays pinned over the dark background and becomes unreadable.
// ---------------------------------------------------------------------------

function applyTheme(el: HTMLElement, theme: ThemeConfig, scheme: 'light' | 'dark'): void {
  if (theme.primaryColor) {
    const direction = shadeDirection(theme.primaryColor);
    if (direction) {
      el.style.setProperty('--po-color-primary', theme.primaryColor);
      el.style.setProperty(
        '--po-color-primary-hover',
        `color-mix(in srgb, ${theme.primaryColor} 88%, ${direction})`,
      );
      el.style.setProperty(
        '--po-color-primary-active',
        `color-mix(in srgb, ${theme.primaryColor} 76%, ${direction})`,
      );
      const foreground = readableForeground(theme.primaryColor);
      if (foreground) {
        el.style.setProperty('--po-color-primary-foreground', foreground);
      }
    }
  }

  const forScheme = (color: string, role: 'background' | 'text'): string =>
    scheme === 'dark' ? (adaptToDark(color, role) ?? color) : color;

  const background =
    theme.backgroundColor && toRgb(theme.backgroundColor)
      ? forScheme(theme.backgroundColor, 'background')
      : null;

  if (background) {
    el.style.setProperty('--po-color-background', background);
  }

  if (theme.textColor && toRgb(theme.textColor)) {
    el.style.setProperty('--po-color-text-primary', forScheme(theme.textColor, 'text'));
  } else if (background) {
    // Without this a host background keeps the scheme's default text color,
    // which can land white-on-white.
    const foreground = readableForeground(background);
    if (foreground) {
      el.style.setProperty('--po-color-text-primary', foreground);
    }
  }

  if (theme.fontFamily) {
    el.style.setProperty('--po-font-family', theme.fontFamily);
  }

  if (theme.borderRadius) {
    el.style.setProperty('--po-radius-lg', theme.borderRadius);
    // Derive smaller radii proportionally
    const parsed = parseFloat(theme.borderRadius);
    if (!Number.isNaN(parsed)) {
      const unit = theme.borderRadius.replace(String(parsed), '') || 'px';
      el.style.setProperty('--po-radius-md', `${Math.round(parsed * 0.67)}${unit}`);
      el.style.setProperty('--po-radius-sm', `${Math.round(parsed * 0.5)}${unit}`);
    }
  }
}

// ---------------------------------------------------------------------------
// resolveColorScheme — resolves 'system' to 'light' or 'dark'
// ---------------------------------------------------------------------------

function resolveColorScheme(scheme: ColorScheme): 'light' | 'dark' {
  if (scheme !== 'system') return scheme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// ---------------------------------------------------------------------------
// ThemeProvider
// ---------------------------------------------------------------------------

interface ThemeProviderProps {
  theme: ThemeConfig;
  /** The widget root DOM element to apply CSS vars on. */
  rootElement: HTMLElement;
  children: ReactNode;
}

export function ThemeProvider({ theme, rootElement, children }: ThemeProviderProps) {
  const scheme = theme.colorScheme ?? 'light';
  const [resolvedScheme, setResolvedScheme] = useState<'light' | 'dark'>(() =>
    resolveColorScheme(scheme),
  );

  // The theme object is static from init, but the applied values depend on the
  // resolved scheme — so this re-runs whenever that flips (including 'system').
  const appliedScheme = useRef<'light' | 'dark' | null>(null);
  if (appliedScheme.current !== resolvedScheme) {
    applyTheme(rootElement, theme, resolvedScheme);
    appliedScheme.current = resolvedScheme;
  }

  // Set/update data-color-scheme attribute
  useEffect(() => {
    const resolved = resolveColorScheme(scheme);
    setResolvedScheme(resolved);
    rootElement.setAttribute('data-color-scheme', resolved);

    // For 'system': listen for OS-level changes
    if (scheme !== 'system') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const next = e.matches ? 'dark' : 'light';
      setResolvedScheme(next);
      rootElement.setAttribute('data-color-scheme', next);
    };

    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [scheme, rootElement]);

  // Build context value — stable unless scheme changes
  const value = useMemo<ThemeContextValue>(
    () => ({ colorScheme: resolvedScheme, logo: theme.logo ?? null }),
    [resolvedScheme, theme.logo],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ---------------------------------------------------------------------------
// useTheme hook
// ---------------------------------------------------------------------------

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a <ThemeProvider>');
  }
  return ctx;
}
